import axios from 'axios';
import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import './StudentReport.css';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface Student {
    id: number;
    name: string;
    date_reg: string;
    guardian_number: string;
    class_associated: number;
    section_associated: string | null;
    class_name: string;
    address: string;
    district: string;
    file_upload: string;
    Fees: string;
    Index_number: string;
    gender: string;
}

interface Class {
    id: number;
    name: string;
    date_created?: string;
    branch?: number;
}

interface Fees {
    id: number;
    student_name: string;
    total_amount: number;
    amount_paid: number;
    balance: number;
    date_paid: string;
    class_name: string;
}

interface Mark {
    id: number;
    student_name: string;
    examination_name: string;
    marks: string;
    file: string;
}

interface Attendance {
    id: number;
    student_name: string;
    date: string;
    status: string;
}

const StudentReport: React.FC = () => {
    const [classes, setClasses] = useState<Class[]>([]);
    const [marks, setMarks] = useState<Mark[]>([]);
    const [students, setStudents] = useState<Student[]>([]);
    const [fees, setFees] = useState<Fees | null>(null);
    const [studentInfo, setStudentInfo] = useState<Student | null>(null);
    const [attendance, setAttendance] = useState<Attendance[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [selectedClass, setSelectedClass] = useState<number | null>(null);
    const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [loading, setLoading] = useState<{ [key: string]: boolean }>({
        classes: false,
        students: false,
        studentInfo: false,
        marks: false,
        fees: false,
        attendance: false
    });

    const setLoadingState = (key: string, value: boolean) => {
        setLoading(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const fetchClasses = async () => {
            setLoadingState('classes', true);
            try {
                const response = await axios.get('/Branches/classes/');
                setClasses(response.data);
                setError(null);
            } catch (error) {
                console.error("Error fetching classes: ", error);
                setError("Error occurred while fetching classes. Please try again later.");
            } finally {
                setLoadingState('classes', false);
            }
        };
        fetchClasses();
    }, []);

    const fetchStudentsByClass = async (classId: number) => {
        setLoadingState('students', true);
        try {
            const response = await axios.get(`/Branches/students/?class_id=${classId}`);
            setStudents(response.data.results || []);
            setError(null);
        } catch (error) {
            console.error("Error fetching students: ", error);
            setError("An error occurred while fetching students.");
            setStudents([]);
        } finally {
            setLoadingState('students', false);
        }
    };

    const fetchAttendanceByStudent = async (studentId: number, date: string | null = null) => {
        setLoadingState('attendance', true);
        try {
            let url = `/Branches/attendance/?student=${studentId}`;
            if (date) {
                url += `&date=${date}`;
            }
            const response = await axios.get(url);
            setAttendance(response.data || []);
            setError(null);
        } catch (error) {
            console.error("Error fetching attendance: ", error);
            setAttendance([]);
        } finally {
            setLoadingState('attendance', false);
        }
    };

    const fetchStudentInfo = async (studentId: number) => {
        setLoadingState('studentInfo', true);
        try {
            const response = await axios.get(`/Branches/students/${studentId}/`);
            setStudentInfo(response.data);
            setError(null);
        } catch (error) {
            console.error("Error fetching student info: ", error);
            setStudentInfo(null);
        } finally {
            setLoadingState('studentInfo', false);
        }
    };

    const fetchMarksByStudent = async (studentId: number) => {
        setLoadingState('marks', true);
        try {
            const response = await axios.get(`/exams/marks/?student=${studentId}`);
            setMarks(response.data || []);
            setError(null);
        } catch (error) {
            console.error("Error fetching marks: ", error);
            setMarks([]);
        } finally {
            setLoadingState('marks', false);
        }
    };

    const fetchFeesByStudent = async (studentId: number) => {
        setLoadingState('fees', true);
        try {
            const response = await axios.get(`/fees/fees/?student=${studentId}`);
            setFees(response.data?.results?.[0] || null);
            setError(null);
        } catch (error) {
            console.error("Error fetching fees: ", error);
            setFees(null);
        } finally {
            setLoadingState('fees', false);
        }
    };

    const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const classId = parseInt(e.target.value);
        setSelectedClass(classId);
        setSelectedStudent(null);
        setFees(null);
        setStudentInfo(null);
        setMarks([]);
        setAttendance([]);
        fetchStudentsByClass(classId);
    };

    const handleStudentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const studentId = parseInt(e.target.value);
        setSelectedStudent(studentId);
        fetchAttendanceByStudent(studentId, selectedDate);
        fetchStudentInfo(studentId);
        fetchMarksByStudent(studentId);
        fetchFeesByStudent(studentId);
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = e.target.value;
        setSelectedDate(date);
        if (selectedStudent) {
            fetchAttendanceByStudent(selectedStudent, date);
        }
    };

    const uniqueAttendanceByDate = (attendances: Attendance[]) => {
        const uniqueMap: { [key: string]: Attendance } = {};
        attendances.forEach((att) => {
            uniqueMap[att.date] = att;
        });
        return Object.values(uniqueMap);
    };

    // Attendance Chart Data
    const getAttendanceChartData = () => {
        const presentCount = attendance.filter(a => a.status.toLowerCase() === 'present').length;
        const absentCount = attendance.filter(a => a.status.toLowerCase() === 'absent').length;

        return {
            labels: ['Present', 'Absent'],
            datasets: [{
                data: [presentCount, absentCount],
                backgroundColor: [
                    'rgba(75, 192, 192, 0.8)',
                    'rgba(255, 99, 132, 0.8)',
                ],
                borderColor: [
                    'rgba(75, 192, 192, 1)',
                    'rgba(255, 99, 132, 1)',
                ],
                borderWidth: 2,
            }],
        };
    };

    // Marks Trend Chart Data
    const getMarksChartData = () => {
        return {
            labels: marks.map(m => m.examination_name),
            datasets: [{
                label: 'Isbedelka Dhibcaha',
                data: marks.map(m => parseFloat(m.marks) || 0),
                fill: true,
                backgroundColor: 'rgba(102, 126, 234, 0.2)',
                borderColor: 'rgb(102, 126, 234)',
                tension: 0.4,
                pointRadius: 6,
                pointHoverRadius: 8,
            }],
        };
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadPDF = () => {
        const doc = new jsPDF();
        let yPosition = 10;

        doc.text("Warbixinta Ardayga", 14, yPosition);
        yPosition += 10;

        if (studentInfo) {
            doc.text(`Magaca: ${studentInfo.name}`, 14, yPosition);
            yPosition += 7;
            doc.text(`Lambarka Diiwaangelinta: ${studentInfo.Index_number}`, 14, yPosition);
            yPosition += 10;
        }

        if (fees) {
            doc.text("Faahfaahinta Lacagaha", 14, yPosition);
            yPosition += 7;
            doc.autoTable({
                startY: yPosition,
                head: [['Wadarta Lacagta', 'Inta La Bixiyay', 'Harta', 'Taariikhda Bixinta']],
                body: [[fees.total_amount, fees.amount_paid, fees.balance, fees.date_paid]]
            });
            yPosition = (doc as any).lastAutoTable.finalY + 10;
        }

        if (marks.length > 0) {
            doc.text("Natiijooyinka", 14, yPosition);
            yPosition += 7;
            doc.autoTable({
                startY: yPosition,
                head: [['Magaca Imtixaanka', 'Dhibcaha']],
                body: marks.map(mark => [mark.examination_name, mark.marks])
            });
            yPosition = (doc as any).lastAutoTable.finalY + 10;
        }

        if (attendance.length > 0) {
            doc.text("Xaadirinta", 14, yPosition);
            yPosition += 7;
            doc.autoTable({
                startY: yPosition,
                head: [['Taariikhda', 'Xaaladda']],
                body: uniqueAttendanceByDate(attendance).map(att => [att.date, att.status])
            });
        }

        doc.save('Warbixinta_Ardayga.pdf');
    };

    const handleExportExcel = () => {
        const workbook = XLSX.utils.book_new();

        if (studentInfo) {
            const studentData = [{
                'Magaca': studentInfo.name,
                'Lambarka Diiwaangelinta': studentInfo.Index_number,
                'Lambarka Waalidka': studentInfo.guardian_number,
                'Cinwaanka': studentInfo.address,
                'Degmada': studentInfo.district
            }];
            const studentSheet = XLSX.utils.json_to_sheet(studentData);
            XLSX.utils.book_append_sheet(workbook, studentSheet, "Macluumaadka Ardayga");
        }

        if (fees) {
            const feesData = [{
                'Wadarta Lacagta': fees.total_amount,
                'Inta La Bixiyay': fees.amount_paid,
                'Harta': fees.balance,
                'Taariikhda Bixinta': fees.date_paid
            }];
            const feesSheet = XLSX.utils.json_to_sheet(feesData);
            XLSX.utils.book_append_sheet(workbook, feesSheet, "Lacagaha");
        }

        if (marks.length > 0) {
            const marksData = marks.map(mark => ({
                'Magaca Imtixaanka': mark.examination_name,
                'Dhibcaha': mark.marks
            }));
            const marksSheet = XLSX.utils.json_to_sheet(marksData);
            XLSX.utils.book_append_sheet(workbook, marksSheet, "Natiijooyinka");
        }

        if (attendance.length > 0) {
            const attendanceData = uniqueAttendanceByDate(attendance).map(att => ({
                'Taariikhda': att.date,
                'Xaaladda': att.status
            }));
            const attendanceSheet = XLSX.utils.json_to_sheet(attendanceData);
            XLSX.utils.book_append_sheet(workbook, attendanceSheet, "Xaadirinta");
        }

        XLSX.writeFile(workbook, 'Warbixinta_Ardayga.xlsx');
    };

    return (
        <div className="student-report-container">
            {/* Hero Header */}
            <div className="report-hero">
                <div className="hero-content">
                    <h1 className="hero-title">
                        <span className="icon">📊</span> Warbixinta Ardayga
                    </h1>
                    <p className="hero-subtitle">Macluumaad dhameystiran, waxqabadka iyo xaaladda ardayga</p>
                </div>
            </div>

            {error && <div className="alert alert-danger m-4">{error}</div>}

            {/* Filters Section */}
            <div className="filters-card">
                <h3 className="filters-title">
                    <span className="icon-small">🔍</span> Dooro Arday
                </h3>
                <div className="filters-grid">
                    <div className="filter-group">
                        <label className="filter-label">Fasalka</label>
                        <select
                            className="filter-select"
                            value={selectedClass || ''}
                            onChange={handleClassChange}
                        >
                            <option value="" disabled>Dooro Fasal</option>
                            {classes.map((classItem) => (
                                <option key={classItem.id} value={classItem.id}>
                                    {classItem.name}
                                </option>
                            ))}
                        </select>
                        {loading.classes && <div className="loading-text">Soo dejinaya fasallada...</div>}
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Ardayga</label>
                        <select
                            className="filter-select"
                            value={selectedStudent || ''}
                            onChange={handleStudentChange}
                            disabled={!selectedClass || loading.students}
                        >
                            <option value="" disabled>Dooro Arday</option>
                            {students.map((student) => (
                                <option key={student.id} value={student.id}>
                                    {student.name}
                                </option>
                            ))}
                        </select>
                        {loading.students && <div className="loading-text">Soo dejinaya ardayda...</div>}
                        {!loading.students && students.length === 0 && selectedClass && (
                            <div className="warning-text">Lama helin arday fasalkan</div>
                        )}
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Shaandhee Taariikhda</label>
                        <input
                            type="date"
                            className="filter-select"
                            value={selectedDate || ''}
                            onChange={handleDateChange}
                            disabled={!selectedStudent}
                        />
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            {selectedStudent && (
                <div className="action-buttons">
                    <button className="action-btn btn-print" onClick={handlePrint}>
                        <span className="btn-icon">🖨️</span> Daabac
                    </button>
                    <button className="action-btn btn-pdf" onClick={handleDownloadPDF}>
                        <span className="btn-icon">📄</span> PDF
                    </button>
                    <button className="action-btn btn-excel" onClick={handleExportExcel}>
                        <span className="btn-icon">📊</span> Excel
                    </button>
                </div>
            )}

            {/* Student Details Section */}
            {selectedStudent && (
                <>
                    <div className="info-grid">
                        {/* Student Info Card */}
                        <div className="info-card">
                            <div className="card-header gradient-purple">
                                <span className="card-icon">👤</span>
                                <h3 className="card-title">Macluumaadka Ardayga</h3>
                            </div>
                            <div className="card-body">
                                {loading.studentInfo ? (
                                    <div className="loading-state">Soo dejinaya macluumaadka ardayga...</div>
                                ) : studentInfo ? (
                                    <div className="info-list">
                                        <div className="info-item">
                                            <span className="info-label">Magaca:</span>
                                            <span className="info-value">{studentInfo.name}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Lambarka Diiwaangelinta:</span>
                                            <span className="info-value">{studentInfo.Index_number}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Lambarka Waalidka:</span>
                                            <span className="info-value">{studentInfo.guardian_number}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Cinwaanka:</span>
                                            <span className="info-value">{studentInfo.address}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Degmada:</span>
                                            <span className="info-value">{studentInfo.district}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="empty-state">Macluumaadka ardayga lama helin</div>
                                )}
                            </div>
                        </div>

                        {/* Fee Details Card */}
                        <div className="info-card">
                            <div className="card-header gradient-green">
                                <span className="card-icon">💰</span>
                                <h3 className="card-title">Faahfaahinta Lacagaha</h3>
                            </div>
                            <div className="card-body">
                                {loading.fees ? (
                                    <div className="loading-state">Soo dejinaya faahfaahinta lacagaha...</div>
                                ) : fees ? (
                                    <div className="info-list">
                                        <div className="info-item">
                                            <span className="info-label">Wadarta Lacagta:</span>
                                            <span className="info-value highlight">KSh {fees.total_amount}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Inta La Bixiyay:</span>
                                            <span className="info-value success">KSh {fees.amount_paid}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Harta:</span>
                                            <span className="info-value warning">KSh {fees.balance}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Taariikhda Bixinta:</span>
                                            <span className="info-value">{fees.date_paid}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="empty-state">Macluumaadka lacagaha lama helin</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Charts Row */}
                    <div className="charts-grid">
                        {/* Marks Chart */}
                        {marks.length > 0 && (
                            <div className="chart-card">
                                <div className="card-header gradient-blue">
                                    <span className="card-icon">📈</span>
                                    <h3 className="card-title">Isbedelka Dhibcaha</h3>
                                </div>
                                <div className="card-body">
                                    <Line
                                        data={getMarksChartData()}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: true,
                                            plugins: {
                                                legend: {
                                                    display: false,
                                                },
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Attendance Chart */}
                        {attendance.length > 0 && (
                            <div className="chart-card">
                                <div className="card-header gradient-pink">
                                    <span className="card-icon">📅</span>
                                    <h3 className="card-title">Guudmarka Xaadirinta</h3>
                                </div>
                                <div className="card-body center-content">
                                    <div className="chart-wrapper">
                                        <Doughnut
                                            data={getAttendanceChartData()}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: true,
                                                plugins: {
                                                    legend: {
                                                        position: 'bottom',
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Detailed Marks */}
                    {marks.length > 0 && (
                        <div className="details-card">
                            <div className="card-header gradient-indigo">
                                <span className="card-icon">📝</span>
                                <h3 className="card-title">Dhibcaha Imtixaanka</h3>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Imtixaanka</th>
                                                <th>Dhibcaha</th>
                                                <th>Ficil</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {marks.map(mark => (
                                                <tr key={mark.id}>
                                                    <td className="fw-600">{mark.examination_name}</td>
                                                    <td>
                                                        <span className="badge-marks">{mark.marks}</span>
                                                    </td>
                                                    <td>
                                                        {mark.file && (
                                                            <a
                                                                href={mark.file}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="download-btn"
                                                            >
                                                                ⬇️ Soo Degso
                                                            </a>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Detailed Attendance */}
                    {attendance.length > 0 && (
                        <div className="details-card">
                            <div className="card-header gradient-orange">
                                <span className="card-icon">📋</span>
                                <h3 className="card-title">Diiwaanka Xaadirinta</h3>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Taariikhda</th>
                                                <th>Xaaladda</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {uniqueAttendanceByDate(attendance).map(att => (
                                                <tr key={att.id}>
                                                    <td className="fw-600">{att.date}</td>
                                                    <td>
                                                        <span className={`status-badge ${att.status.toLowerCase()}`}>
                                                            {att.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default StudentReport;